import { Link } from "react-router-dom";
import { Building2, Users, Wallet, Target, AlertTriangle, ArrowUpRight } from "lucide-react";
import { BirthdayPanel, TodayBirthdayBanner } from "@/components/Birthdays";
import { useStore } from "@/store/AppStore";
import { PageHeader, Panel, KpiCard, ProgressLine, Tag, attTone } from "@/components/kit";
import { BarsChart, LinesChart, DonutChart, GOLD, INK, GREY } from "@/components/Charts";
import { companySummary, companyHistory, pct } from "@/lib/selectors";
import { MONTH_LABEL, MONTHS } from "@/data/mock";
import { brl } from "@/lib/format";

export default function MasterOverview() {
  const { state } = useStore();
  const rows = state.companies.map((c) => companySummary(state, c));
  const totalSales = rows.reduce((a, r) => a + r.sales, 0);
  const totalGoal = rows.reduce((a, r) => a + r.salesGoal, 0);
  const histories = state.companies.map((c) => companyHistory(state, c));
  const trend = MONTHS.map((mes, i) => ({ mes, vendas: histories.reduce((a, h) => a + h[i].vendas, 0), meta: totalGoal }));
  const alerts = rows.filter((r) => r.att < 90 || r.taskRate < 50);

  return (
    <div data-testid="master-overview-page">
      <PageHeader eyebrow={MONTH_LABEL} title="Visão geral da consultoria" subtitle="Acompanhe o desempenho de cada empresa atendida. Os dados de cada empresa permanecem separados." />
      <TodayBirthdayBanner companyIds={state.companies.map((c) => c.id)} />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard testId="kpi-companies" icon={Building2} label="Empresas" value={rows.length} hint="Atendidas ativamente" />
        <KpiCard testId="kpi-team" icon={Users} label="Colaboradoras" value={state.collaborators.length} hint="Em todas as empresas" />
        <KpiCard testId="kpi-sales" icon={Wallet} label="Vendas do mês" value={brl(totalSales)} hint={`Meta consolidada ${brl(totalGoal)}`} />
        <KpiCard testId="kpi-goal" icon={Target} label="Meta atingida" value={`${pct(totalSales, totalGoal)}%`} hint="Média ponderada" />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel title="Vendas x meta por empresa" subtitle="Mês atual" className="lg:col-span-2" testId="chart-sales-by-company">
          <BarsChart data={rows.map((r) => ({ nome: r.name, vendas: r.sales, meta: r.salesGoal }))} xKey="nome" series={[{ key: "vendas", name: "Vendas", color: GOLD }, { key: "meta", name: "Meta", color: GREY }]} />
        </Panel>
        <Panel title="Participação nas vendas" subtitle="Distribuição entre empresas" testId="chart-sales-share">
          <DonutChart data={rows.map((r) => ({ name: r.name, value: r.sales }))} />
        </Panel>
        <Panel title="Evolução consolidada" subtitle="Últimos 6 meses" className="lg:col-span-2" testId="chart-trend">
          <LinesChart data={trend} xKey="mes" series={[{ key: "vendas", name: "Vendas", color: INK }, { key: "meta", name: "Meta atual", color: GOLD, dashed: true }]} />
        </Panel>
        <Panel title="Pontos de atenção" subtitle="Empresas que precisam de acompanhamento" testId="alerts-panel">
          <ul className="space-y-4">
            {alerts.map((r) => (
              <li key={r.id} className="flex gap-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <p className="text-sm text-neutral-700"><span className="font-semibold">{r.name}</span>: {r.att < 90 ? `meta em ${r.att}%` : ""}{r.att < 90 && r.taskRate < 50 ? " · " : ""}{r.taskRate < 50 ? `tarefas em ${r.taskRate}%` : ""}</p>
              </li>
            ))}
            {!alerts.length && <p className="text-sm text-neutral-400">Tudo em dia.</p>}
          </ul>
        </Panel>
      </div>
      <BirthdayPanel companyIds={state.companies.map((c) => c.id)} showCompany className="mt-6" />
      <Panel title="Empresas atendidas" className="mt-6" testId="companies-summary-table" action={<Link to="/master/empresas" className="text-xs font-semibold text-gold-dark hover:underline" data-testid="see-all-companies-link">Ver todas</Link>}>
        <div className="divide-y divide-neutral-100">
          {rows.map((r) => (
            <Link key={r.id} to={`/master/empresas/${r.id}`} data-testid={`company-row-${r.id}`} className="group grid grid-cols-2 items-center gap-4 py-4 sm:grid-cols-[2fr_1fr_1fr_1.5fr_auto]">
              <div><p className="text-sm font-semibold text-neutral-900">{r.name}</p><p className="text-xs text-neutral-500">{r.segment}</p></div>
              <p className="text-right text-sm font-semibold sm:text-left">{brl(r.sales)}</p>
              <p className="hidden text-sm text-neutral-600 sm:block">{r.team} colaboradoras</p>
              <div className="col-span-2 flex items-center gap-3 sm:col-span-1"><ProgressLine value={r.att} /><Tag tone={attTone(r.att)}>{r.att}%</Tag></div>
              <ArrowUpRight className="hidden h-4 w-4 text-neutral-300 transition-colors group-hover:text-gold sm:block" />
            </Link>
          ))}
        </div>
      </Panel>
    </div>
  );
}
